import axios from 'axios';
import * as Sentry from '@sentry/react';
import { trackEvent } from './telemetry';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  withCredentials: true,
});

const getCookie = (name: string) => {
  const match = document.cookie
    .split(';')
    .map(entry => entry.trim())
    .find(entry => entry.startsWith(`${name}=`));
  if (!match) return '';
  return decodeURIComponent(match.slice(name.length + 1));
};

const authFreeEndpoints = [
  '/auth/login',
  '/auth/register',
  '/auth/resend-verification',
  '/auth/verify-email',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/social-session',
  '/auth/me',
  '/auth/logout',
  '/auth/passkey/authenticate',
];

api.interceptors.request.use(config => {
  (config as any).metadata = { startTime: Date.now() };
  const method = (config.method || 'get').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCookie('csrf_token');
    if (csrfToken) {
      config.headers = config.headers || {};
      config.headers['x-csrf-token'] = csrfToken;
    }
  }
  return config;
});

api.interceptors.response.use(
  response => {
    const url = response.config?.url || '';
    const startTime = (response.config as any)?.metadata?.startTime;
    if (!url.includes('/telemetry') && startTime) {
      trackEvent({
        event: 'api_request',
        properties: {
          method: response.config?.method,
          url,
          status: response.status,
          duration_ms: Date.now() - startTime,
        },
      });
    }
    return response;
  },
  error => {
    const url = error.config?.url || '';
    const startTime = (error.config as any)?.metadata?.startTime;
    if (startTime && !url.includes('/telemetry')) {
      trackEvent({
        event: 'api_request_error',
        properties: {
          method: error.config?.method,
          url,
          status: error.response?.status,
          duration_ms: Date.now() - startTime,
          message: error.message,
        },
      });
    }
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    const hasUser = Boolean(localStorage.getItem('user'));
    const isAuthFree = authFreeEndpoints.some(endpoint => requestUrl.includes(endpoint));

    if ((status === 401 || status === 403) && hasUser && !isAuthFree) {
      localStorage.removeItem('user');

      if (!window.location.pathname.startsWith('/login')) {
        const returnTo = `${window.location.pathname}${window.location.search}`;
        window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
      }
    }

    if (status && status >= 500) {
      Sentry.captureException(error, {
        extra: { url: requestUrl, method: error.config?.method, status },
      });
    } else if (!error.response && error.message) {
      Sentry.captureException(error, {
        extra: { url: requestUrl, method: error.config?.method },
      });
    }

    return Promise.reject(error);
  }
);

export default api;

export const getApiBaseUrl = (): string => (api.defaults.baseURL as string) || '';
export const getCsrfToken = (): string => getCookie('csrf_token');

